import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trendsAPI, sitesAPI } from '../services/apiEndpoints';
import { TREND_LOCATIONS, DEFAULT_LOCATION } from '../data/trendLocations';
import QuickPost from './QuickPost';
import KeywordInput from './KeywordInput';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
    MagnifyingGlassIcon, RocketIcon, BarChartIcon, PlusIcon, ReloadIcon,
    InfoCircledIcon, LightningBoltIcon, ArrowUpIcon,
    CheckCircledIcon, DoubleArrowUpIcon, ViewGridIcon, TableIcon,
    BellIcon, Cross2Icon, GearIcon, ArrowRightIcon
} from '@radix-ui/react-icons';

const MODE_KEYWORD  = 'keyword';
const MODE_CATEGORY = 'category';

function TrendDiscovery({ user }) {
    const navigate = useNavigate();

    const [mode, setMode] = useState(MODE_KEYWORD);

    // ── Keyword-mode state ────────────────────────────────────────────────
    const [keywords, setKeywords]               = useState([]);
    const [kwLocation, setKwLocation]           = useState(DEFAULT_LOCATION);
    const [kwRecentSearches, setKwRecentSearches] = useState([]);

    // ── Category-mode state ───────────────────────────────────────────────
    const [sites, setSites]                     = useState([]);
    const [selectedSite, setSelectedSite]       = useState('');
    const [categories, setCategories]           = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [catLocation, setCatLocation]         = useState(DEFAULT_LOCATION);
    const [loadingSites, setLoadingSites]       = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [catRecentSearches, setCatRecentSearches] = useState([]);

    // ── Shared state ──────────────────────────────────────────────────────
    const [loading, setLoading]                 = useState(false);
    const [error, setError]                     = useState(null);
    const [results, setResults]                 = useState(null);   // { topics[], meta }
    const [filterLevel, setFilterLevel]         = useState('all');
    const [difficultyFilter, setDifficultyFilter] = useState('all');
    const [sortBy, setSortBy]                   = useState('growth');
    const [viewMode, setViewMode]               = useState('cards');
    const [showQuickPost, setShowQuickPost]     = useState(false);
    const [prefilledTopic, setPrefilledTopic]   = useState('');
    const [prefilledLocation, setPrefilledLocation] = useState('');

    // ── Init ──────────────────────────────────────────────────────────────
    useEffect(() => {
        loadKwRecentSearches();
        loadCatRecentSearches();
        loadWordPressSites();
    }, []);

    useEffect(() => {
        if (selectedSite) loadCategories(selectedSite);
        else { setCategories([]); setSelectedCategory(''); }
    }, [selectedSite]);

    const loadWordPressSites = async () => {
        setLoadingSites(true);
        try { const r = await sitesAPI.getWordPressSites(); setSites(r.data.sites || []); }
        catch { /* non-critical */ }
        finally { setLoadingSites(false); }
    };

    const loadCategories = async (siteId) => {
        setLoadingCategories(true); setSelectedCategory('');
        try { const r = await sitesAPI.getSiteCategories(siteId); setCategories(r.data.categories || []); }
        catch { /* non-critical */ }
        finally { setLoadingCategories(false); }
    };

    const loadKwRecentSearches = async () => {
        try { const r = await trendsAPI.getRecentSearches(); if (r.data.success) setKwRecentSearches(r.data.data || []); }
        catch { /* non-critical */ }
    };

    const loadCatRecentSearches = async () => {
        try { const r = await trendsAPI.getRecentCategories(); if (r.data.success) setCatRecentSearches(r.data.data || []); }
        catch { /* non-critical */ }
    };

    // ── Helpers ───────────────────────────────────────────────────────────
    const getGrowthLevel = (value) => {
        const n = parseInt(value);
        if (n >= 1000) return { icon: LightningBoltIcon, label: 'EXPLOSIVE', class: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-500/20' };
        if (n >= 100)  return { icon: DoubleArrowUpIcon,  label: 'STRONG',   class: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' };
        if (n >= 50)   return { icon: CheckCircledIcon,   label: 'GOOD',     class: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/20' };
        return { icon: ArrowUpIcon, label: 'MODERATE', class: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/20' };
    };

    const getDifficultyLevel = (d) => {
        if (d === null || d === undefined) return { label: 'N/A', class: 'text-zinc-400', bg: 'bg-zinc-500/20' };
        if (d < 30) return { label: 'Easy',   class: 'text-emerald-400', bg: 'bg-emerald-500/20' };
        if (d < 60) return { label: 'Medium', class: 'text-amber-400',   bg: 'bg-amber-500/20' };
        return { label: 'Hard', class: 'text-rose-400', bg: 'bg-rose-500/20' };
    };

    const getDifficultyBadge = (d) => {
        if (d === null || d === undefined) return { label: 'N/A',    className: 'bg-zinc-700/50 text-zinc-400 border-zinc-600/50' };
        if (d <= 30) return { label: 'Easy',   className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' };
        if (d <= 60) return { label: 'Medium', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
        return { label: 'Hard', className: 'bg-red-500/20 text-red-400 border-red-500/30' };
    };

    const getFiltered = () => {
        if (!results?.topics) return [];
        let list = results.topics;
        if (filterLevel !== 'all') list = list.filter(t => getGrowthLevel(t.growth).label.toLowerCase() === filterLevel.toLowerCase());
        if (difficultyFilter !== 'all') {
            list = list.filter(t => {
                const d = t.difficulty;
                if (d === null || d === undefined) return false;
                if (difficultyFilter === 'easy')   return d < 30;
                if (difficultyFilter === 'medium') return d >= 30 && d < 60;
                if (difficultyFilter === 'hard')   return d >= 60;
                return true;
            });
        }
        if (sortBy === 'growth') list = [...list].sort((a, b) => b.growth - a.growth);
        else list = [...list].sort((a, b) => ({ High: 3, Medium: 2, Low: 1 }[b.volume] - { High: 3, Medium: 2, Low: 1 }[a.volume]));
        return list;
    };

    const handleAddToQueue = (topic) => {
        setPrefilledTopic(topic);
        setPrefilledLocation(results?.meta?.location || DEFAULT_LOCATION);
        setShowQuickPost(true);
    };

    const handleDismiss = (id) => setResults(prev => prev ? { ...prev, topics: prev.topics.filter(t => t.id !== id) } : prev);
    const handleSnooze  = (id) => setResults(prev => prev ? { ...prev, topics: prev.topics.map(t => t.id === id ? { ...t, isNew: false } : t) } : prev);

    // ── API calls ─────────────────────────────────────────────────────────

    const discoverByKeyword = async (kw = null, loc = null, force = false, fromRecent = false) => {
        const kwList = kw || keywords;
        const locVal = loc || kwLocation;
        const primary = Array.isArray(kwList) ? kwList[0] : kwList;
        if (!primary) { setError('Please enter a keyword to discover trending topics'); return; }
        setLoading(true); setError(null);
        try {
            const res = await trendsAPI.discoverTrending(primary, locVal, force, fromRecent);
            if (res.data.success) {
                const d = res.data.data;
                const topics = d.rising_queries.map((q, idx) => ({
                    id:         idx + 1,
                    keyword:    q.query,
                    topic:      q.query,
                    growth:     q.value,
                    growthLabel: `+${q.value}%`,
                    volume:     q.value > 500 ? 'High' : q.value > 100 ? 'Medium' : 'Low',
                    interest:   Math.min(100, Math.floor(q.value / 10)),
                    difficulty: q.difficulty,
                    isNew:      false,
                }));
                setResults({ topics, meta: { label: primary, location: locVal, dataAge: d.data_age, isFresh: d.is_fresh, type: 'keyword' } });
                loadKwRecentSearches();
            } else {
                setError(res.data.message || 'Failed to discover trends');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to discover trends. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const discoverByCategory = async (catName = null, locCode = null, force = false, fromRecent = false) => {
        const locVal = locCode || catLocation;
        let categoryName = catName;
        if (!categoryName) {
            if (!selectedCategory) { setError('Please select a category'); return; }
            const cat = categories.find(c => c.id.toString() === selectedCategory);
            categoryName = cat?.name;
            if (!categoryName) { setError('Category not found'); return; }
        }
        setLoading(true); setError(null);
        try {
            const res = await trendsAPI.discoverTrendAlerts(categoryName, locVal, force, fromRecent);
            if (res.data.success) {
                const d = res.data.data;
                const topics = d.rising_queries.map((q, idx) => ({
                    id:         idx + 1,
                    keyword:    q.query,
                    topic:      q.query,
                    growth:     q.value,
                    growthLabel: `+${q.value}%`,
                    volume:     q.value > 500 ? 'High' : q.value > 100 ? 'Medium' : 'Low',
                    interest:   Math.min(100, Math.floor(q.value / 10)),
                    difficulty: q.difficulty,
                    isNew:      idx < 3,
                    trending:   [TREND_LOCATIONS.find(l => l.code === locVal)?.name || 'USA'],
                    category:   categoryName,
                }));
                setResults({ topics, meta: { label: categoryName, location: locVal, dataAge: d.data_age, isFresh: d.is_fresh, type: 'category' } });
                loadCatRecentSearches();
            } else {
                setError(res.data.message || 'Failed to fetch trending alerts');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to fetch trending alerts. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        if (!results) return;
        if (results.meta.type === 'keyword') discoverByKeyword(results.meta.label, results.meta.location, true);
        else discoverByCategory(results.meta.label, results.meta.location, true);
    };

    // ── Sub-components ────────────────────────────────────────────────────

    const FilterChip = ({ label, active, onClick, color }) => {
        const colors = {
            fuchsia: active ? 'bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/50' : 'bg-zinc-900/50 text-fuchsia-400/50 border-fuchsia-500/20 hover:border-fuchsia-500/40 hover:text-fuchsia-400',
            emerald: active ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' : 'bg-zinc-900/50 text-emerald-400/50 border-emerald-500/20 hover:border-emerald-500/40 hover:text-emerald-400',
            blue:    active ? 'bg-blue-500/20 text-blue-400 border-blue-500/50'          : 'bg-zinc-900/50 text-blue-400/50 border-blue-500/20 hover:border-blue-500/40 hover:text-blue-400',
            green:   active ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' : 'bg-zinc-900/50 text-emerald-400/50 border-emerald-500/20 hover:border-emerald-500/40 hover:text-emerald-400',
            amber:   active ? 'bg-amber-500/20 text-amber-400 border-amber-500/50'       : 'bg-zinc-900/50 text-amber-400/50 border-amber-500/20 hover:border-amber-500/40 hover:text-amber-400',
            rose:    active ? 'bg-rose-500/20 text-rose-400 border-rose-500/50'          : 'bg-zinc-900/50 text-rose-400/50 border-rose-500/20 hover:border-rose-500/40 hover:text-rose-400',
        };
        const cls = color ? colors[color] : active ? 'bg-white text-zinc-950 border-white shadow-lg shadow-white/10' : 'bg-zinc-900/50 text-zinc-400 border-white/10 hover:border-white/30 hover:text-zinc-200';
        return (
            <button onClick={onClick} className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${cls}`}>
                {label}
            </button>
        );
    };

    const NewAlertCard = ({ topic }) => {
        const db = getDifficultyBadge(topic.difficulty);
        return (
            <Card className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/30">
                <CardHeader>
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                            <BellIcon className="w-5 h-5 text-orange-400" />
                            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">TRENDING NOW</Badge>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => handleDismiss(topic.id)} className="text-zinc-400 hover:text-red-400">
                            <Cross2Icon className="w-4 h-4" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <h3 className="text-xl font-bold text-white">"{topic.topic}"</h3>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="flex items-center gap-2">
                            <LightningBoltIcon className="w-4 h-4 text-orange-400" />
                            <div><div className="text-xs text-zinc-500">Growth</div><div className="text-sm font-bold text-orange-400">+{topic.growth.toLocaleString()}%</div></div>
                        </div>
                        <div className="flex items-center gap-2">
                            <RocketIcon className="w-4 h-4 text-blue-400" />
                            <div><div className="text-xs text-zinc-500">Volume</div><div className="text-sm font-bold text-blue-400">{topic.volume}</div></div>
                        </div>
                        <div className="flex items-center gap-2">
                            <GearIcon className="w-4 h-4 text-zinc-400" />
                            <div>
                                <div className="text-xs text-zinc-500">Difficulty</div>
                                <div className="flex items-center gap-1">
                                    {topic.difficulty != null ? <span className="text-sm font-bold text-white">{topic.difficulty}</span> : null}
                                    <Badge className={`text-xs px-1.5 py-0 ${db.className}`}>{db.label}</Badge>
                                </div>
                            </div>
                        </div>
                    </div>
                    {topic.trending?.length > 0 && (
                        <div>
                            <div className="text-xs text-zinc-500 mb-1">🌍 Trending in:</div>
                            <div className="flex gap-2 flex-wrap">
                                {topic.trending.map((loc, i) => (
                                    <Badge key={i} className="bg-zinc-800 text-zinc-300 border-zinc-700">{loc}</Badge>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                        <div className="flex items-start gap-2">
                            <span className="text-lg">💡</span>
                            <div>
                                <div className="font-semibold text-white mb-1">Opportunity Detected</div>
                                <p className="text-sm text-zinc-300">This topic is gaining momentum. Create content now to capture early traffic!</p>
                            </div>
                        </div>
                    </div>
                    <Button onClick={() => handleAddToQueue(topic.topic)} className="w-full bg-white/5 backdrop-blur-sm border border-white/20 hover:bg-white/10 hover:border-white/30 text-white gap-2 transition-all">
                        <PlusIcon className="w-4 h-4" /> Add to Queue
                    </Button>
                </CardContent>
            </Card>
        );
    };

    const ActiveAlertItem = ({ topic }) => {
        const db = getDifficultyBadge(topic.difficulty);
        return (
            <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg hover:bg-zinc-800/50 transition-all group relative">
                <div className="flex items-center gap-3 flex-1 group-hover:mr-20 transition-all duration-300">
                    <LightningBoltIcon className="w-4 h-4 text-yellow-400 shrink-0" />
                    <div className="flex-1 text-white font-medium text-sm">"{topic.topic}"</div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">+{topic.growth}%</Badge>
                        <Badge className={`text-xs ${db.className}`}>KD: {topic.difficulty ?? 'N/A'}</Badge>
                    </div>
                </div>
                <div className="flex gap-1 absolute right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-violet-400" onClick={() => handleAddToQueue(topic.topic)} title="Add to Queue">
                        <PlusIcon className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-red-400" onClick={() => handleDismiss(topic.id)}>
                        <Cross2Icon className="w-3 h-3" />
                    </Button>
                </div>
            </div>
        );
    };

    const filtered = getFiltered();
    const newAlerts    = filtered.filter(t => t.isNew);
    const activeAlerts = filtered.filter(t => !t.isNew);

    // ── Render ────────────────────────────────────────────────────────────

    return (
        <div className="text-zinc-50">
                <main className="flex-1 min-h-0 p-6 overflow-y-auto custom-scrollbar">
                    <div className="max-w-7xl mx-auto space-y-5">

                        {/* Mode toggle */}
                        <div className="flex items-center justify-center gap-2">
                            <div className="flex bg-zinc-900/50 border border-white/10 p-1 rounded-xl gap-1">
                                <button
                                    onClick={() => { setMode(MODE_KEYWORD); setResults(null); setError(null); }}
                                    className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${mode === MODE_KEYWORD ? 'bg-white text-zinc-950 shadow-lg' : 'text-zinc-400 hover:text-zinc-200'}`}
                                >
                                    <MagnifyingGlassIcon className="w-4 h-4" /> By Keyword
                                </button>
                                <button
                                    onClick={() => { setMode(MODE_CATEGORY); setResults(null); setError(null); }}
                                    className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${mode === MODE_CATEGORY ? 'bg-white text-zinc-950 shadow-lg' : 'text-zinc-400 hover:text-zinc-200'}`}
                                >
                                    <BellIcon className="w-4 h-4" /> By Category
                                </button>
                            </div>
                        </div>

                        {/* ── Keyword mode input ── */}
                        {mode === MODE_KEYWORD && (
                            <div className="space-y-3">
                                <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-3 flex items-start gap-4 shadow-lg flex-wrap">
                                    <div className="flex-1 min-w-[300px]">
                                        <KeywordInput
                                            keywords={keywords}
                                            onChange={(kws) => { setKeywords(kws); setError(null); }}
                                            onSearch={(term, isErr) => { if (isErr) setError(term); else { setError(null); discoverByKeyword(term); } }}
                                            onInputChange={() => setError(null)}
                                            loading={loading}
                                            placeholder="Enter a topic (e.g., 'Technology', 'Fitness', 'Gaming')..."
                                            location={kwLocation}
                                        />
                                    </div>
                                    <div className="flex items-center gap-3 pt-0">
                                        <Select value={kwLocation} onValueChange={setKwLocation}>
                                            <SelectTrigger className="w-[140px] h-[2.6rem] border border-white/10 bg-zinc-900/50 hover:bg-white/5 focus:ring-0 text-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {TREND_LOCATIONS.map(loc => (
                                                    <SelectItem key={loc.code} value={loc.code}>{loc.flag} {loc.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <button
                                            onClick={() => { if (!keywords.length) { setError('Please enter a keyword'); return; } setError(null); discoverByKeyword(); }}
                                            disabled={loading || keywords.length === 0 || !!error}
                                            className="bg-white text-zinc-950 h-[2.6rem] px-5 rounded-xl font-bold text-xs hover:bg-zinc-200 transition-all shadow-md shadow-white/5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                            {loading ? 'Discovering...' : 'Discover Trends'}
                                        </button>
                                    </div>
                                </div>
                                {kwRecentSearches.length > 0 && (
                                    <div className="flex items-center gap-2 flex-wrap px-1">
                                        <span className="text-xs text-zinc-500 font-medium">Recent:</span>
                                        {kwRecentSearches.slice(0, 5).map((s, i) => (
                                            <Badge key={i} onClick={() => discoverByKeyword(s.keyword, s.location_code, false, true)}
                                                className="cursor-pointer bg-zinc-800/50 hover:bg-zinc-700/50 text-zinc-300 hover:text-white border-white/5 hover:border-white/10 transition-all text-xs px-3 py-1">
                                                <span className="opacity-60 mr-1">{TREND_LOCATIONS.find(l => l.code === s.location_code)?.flag}</span>
                                                <span className="truncate max-w-[100px]">{s.keyword}</span>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Category mode input ── */}
                        {mode === MODE_CATEGORY && (
                            <div className="space-y-3">
                                {!selectedSite && !loading && (
                                    <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                                        <InfoCircledIcon className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                                        <p className="text-xs text-blue-300/80 leading-relaxed">Select a WordPress site to discover trending topics for your category.</p>
                                    </div>
                                )}
                                <div className="flex flex-col md:flex-row items-end gap-4 flex-wrap">
                                    <div className="flex-1 min-w-[180px] group">
                                        <label className="text-xs font-semibold text-zinc-500 mb-2 ml-1 block uppercase tracking-wider group-hover:text-blue-400 transition-colors">WordPress Site</label>
                                        <Select value={selectedSite} onValueChange={setSelectedSite}>
                                            <SelectTrigger className="h-12 rounded-2xl border border-white/10 bg-zinc-900/60 backdrop-blur-md px-4 hover:bg-zinc-800/80 transition-all font-medium">
                                                <SelectValue placeholder="Select Site" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {loadingSites ? <SelectItem value="loading" disabled>Loading...</SelectItem>
                                                    : sites.length === 0 ? <SelectItem value="none" disabled>No sites found</SelectItem>
                                                    : sites.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.site_name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex-1 min-w-[180px] group">
                                        <label className="text-xs font-semibold text-zinc-500 mb-2 ml-1 block uppercase tracking-wider group-hover:text-violet-400 transition-colors">Category</label>
                                        <Select value={categories.find(c => c.id.toString() === selectedCategory) ? selectedCategory : undefined} onValueChange={setSelectedCategory} disabled={!selectedSite || loadingCategories}>
                                            <SelectTrigger className="h-12 rounded-2xl border border-white/10 bg-zinc-900/60 backdrop-blur-md px-4 hover:bg-zinc-800/80 transition-all font-medium disabled:opacity-50">
                                                <SelectValue placeholder={loadingCategories ? 'Loading...' : 'Category'} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {categories.length === 0 ? <SelectItem value="none" disabled>{loadingCategories ? 'Loading...' : 'No categories'}</SelectItem>
                                                    : categories.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="w-full md:w-[200px] group">
                                        <label className="text-xs font-semibold text-zinc-500 mb-2 ml-1 block uppercase tracking-wider group-hover:text-emerald-400 transition-colors">Location</label>
                                        <Select value={catLocation} onValueChange={setCatLocation}>
                                            <SelectTrigger className="h-12 rounded-2xl border border-white/10 bg-zinc-900/60 backdrop-blur-md px-4 hover:bg-zinc-800/80 transition-all font-medium">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {TREND_LOCATIONS.map(loc => (
                                                    <SelectItem key={loc.code} value={loc.code}>{loc.flag} {loc.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="w-full md:w-auto">
                                        <button
                                            onClick={() => discoverByCategory()}
                                            disabled={!selectedCategory || loading}
                                            className="w-full md:w-auto h-12 px-8 bg-white hover:bg-zinc-200 text-zinc-950 rounded-full font-bold text-sm shadow-xl shadow-white/10 hover:shadow-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2 hover:-translate-y-0.5 active:translate-y-0 duration-300"
                                        >
                                            Analyze <ArrowRightIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                {catRecentSearches.length > 0 && (
                                    <div className="flex items-center gap-2 flex-wrap px-1">
                                        <span className="text-xs text-zinc-500 font-medium">Recent:</span>
                                        {catRecentSearches.map((c, i) => (
                                            <Badge key={i} onClick={() => discoverByCategory(c.category_name, c.location_code, false, true)}
                                                className="cursor-pointer bg-zinc-800/50 hover:bg-zinc-700/50 text-zinc-300 hover:text-white border-white/5 hover:border-white/10 transition-all text-xs px-3 py-1">
                                                <span className="opacity-60 mr-1">{TREND_LOCATIONS.find(l => l.code === c.location_code)?.flag}</span>
                                                <span className="truncate max-w-[100px]">{c.category_name}</span>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Error */}
                        {error && (
                            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 flex items-center gap-3">
                                <InfoCircledIcon className="w-5 h-5" /> {error}
                            </div>
                        )}

                        {/* Loading */}
                        {loading && (
                            <div className="py-40 flex flex-col items-center justify-center space-y-6">
                                <div className="relative">
                                    <div className="w-16 h-16 border-4 border-white/5 rounded-full" />
                                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-t-violet-500 rounded-full animate-spin" />
                                </div>
                                <div className="text-center space-y-2">
                                    <h3 className="text-xl font-semibold text-white">{mode === MODE_KEYWORD ? 'Discovering Trending Topics...' : 'Analyzing Trend Alerts...'}</h3>
                                    <p className="text-zinc-500 text-sm italic">"Timing is everything in content creation."</p>
                                </div>
                            </div>
                        )}

                        {/* Empty state */}
                        {!results && !loading && (
                            <div className="space-y-6 py-8">
                                <div className="text-center space-y-3 max-w-xl mx-auto">
                                    <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent">
                                        {mode === MODE_KEYWORD ? '🔥 Trending Topics' : '🔔 Trend Alerts'}
                                    </h1>
                                    <p className="text-zinc-400 text-sm">
                                        {mode === MODE_KEYWORD
                                            ? 'Discover what\'s trending right now in any niche and create timely content.'
                                            : 'Get trending topics for your WordPress categories with keyword difficulty insights.'}
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {(mode === MODE_KEYWORD ? [
                                        { icon: LightningBoltIcon, color: 'from-violet-500/20 to-purple-500/20', iconColor: 'text-violet-400', shadow: 'hover:shadow-violet-500/10', title: 'Real-Time Trends', desc: 'See what\'s exploding in popularity right now with growth percentages.' },
                                        { icon: MagnifyingGlassIcon, color: 'from-blue-500/20 to-cyan-500/20', iconColor: 'text-blue-400', shadow: 'hover:shadow-blue-500/10', title: 'Niche Insights', desc: 'Discover trending topics specific to your niche or industry.' },
                                        { icon: RocketIcon, color: 'from-emerald-500/20 to-green-500/20', iconColor: 'text-emerald-400', shadow: 'hover:shadow-emerald-500/10', title: 'Quick Actions', desc: 'Add trending topics to your queue or generate articles instantly.' },
                                    ] : [
                                        { icon: LightningBoltIcon, color: 'from-orange-500/20 to-red-500/20', iconColor: 'text-orange-400', shadow: 'hover:shadow-orange-500/10', title: 'Category-Based Analysis', desc: 'Get trending topics specific to your website\'s categories.' },
                                        { icon: RocketIcon, color: 'from-blue-500/20 to-cyan-500/20', iconColor: 'text-blue-400', shadow: 'hover:shadow-blue-500/10', title: 'Real-Time Data', desc: 'Powered by Google Trends for accurate, up-to-date insights.' },
                                        { icon: CheckCircledIcon, color: 'from-emerald-500/20 to-green-500/20', iconColor: 'text-emerald-400', shadow: 'hover:shadow-emerald-500/10', title: 'Location-Specific', desc: 'Target trends from specific countries and regions.' },
                                    ]).map(({ icon: Icon, color, iconColor, shadow, title, desc }) => (
                                        <div key={title} className={`group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all hover:shadow-lg ${shadow}`}>
                                            <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                                                <Icon className={`w-6 h-6 ${iconColor}`} />
                                            </div>
                                            <h3 className="text-lg font-semibold text-white mb-1.5">{title}</h3>
                                            <p className="text-zinc-400 text-xs">{desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Results */}
                        {results && !loading && (
                            <div className="space-y-5 animate-fade-in-up">

                                {/* Stats bar */}
                                <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                                    <div className="flex items-center justify-between flex-wrap gap-4">
                                        <div className="flex items-center gap-5 flex-wrap">
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl font-bold text-white">{filtered.length}</span>
                                                <span className="text-sm text-zinc-400">Trending Topics</span>
                                            </div>
                                            <div className="h-5 w-px bg-white/10" />
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-zinc-400">{mode === MODE_KEYWORD ? 'Niche' : 'Category'}:</span>
                                                <span className="text-sm font-medium text-white capitalize">{results.meta.label}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-zinc-400">Location:</span>
                                                <span className="text-sm font-medium text-white">
                                                    {TREND_LOCATIONS.find(l => l.code === results.meta.location)?.flag} {TREND_LOCATIONS.find(l => l.code === results.meta.location)?.name}
                                                </span>
                                            </div>
                                            {results.meta.dataAge && (
                                                <>
                                                    <div className="h-5 w-px bg-white/10" />
                                                    <span className="text-sm text-emerald-400">{results.meta.dataAge}</span>
                                                </>
                                            )}
                                        </div>
                                        <button onClick={handleRefresh} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors">
                                            <ReloadIcon className="w-3.5 h-3.5" /> Refresh
                                        </button>
                                    </div>
                                </div>

                                {/* Filters */}
                                <div className="flex items-center justify-between flex-wrap gap-4">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="text-sm text-zinc-400 font-medium">Growth:</span>
                                        <div className="flex gap-2 flex-wrap">
                                            <FilterChip label="All" active={filterLevel === 'all'} onClick={() => setFilterLevel('all')} />
                                            <FilterChip label="Explosive" active={filterLevel === 'explosive'} onClick={() => setFilterLevel('explosive')} color="fuchsia" />
                                            <FilterChip label="Strong"    active={filterLevel === 'strong'}    onClick={() => setFilterLevel('strong')}    color="emerald" />
                                            <FilterChip label="Good"      active={filterLevel === 'good'}      onClick={() => setFilterLevel('good')}      color="blue" />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="text-sm text-zinc-400 font-medium">Difficulty:</span>
                                        <div className="flex gap-2">
                                            <FilterChip label="All"    active={difficultyFilter === 'all'}    onClick={() => setDifficultyFilter('all')} />
                                            <FilterChip label="Easy"   active={difficultyFilter === 'easy'}   onClick={() => setDifficultyFilter('easy')}   color="green" />
                                            <FilterChip label="Medium" active={difficultyFilter === 'medium'} onClick={() => setDifficultyFilter('medium')} color="amber" />
                                            <FilterChip label="Hard"   active={difficultyFilter === 'hard'}   onClick={() => setDifficultyFilter('hard')}   color="rose" />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 ml-auto">
                                        <Select value={sortBy} onValueChange={setSortBy}>
                                            <SelectTrigger className="w-[130px] h-8 bg-zinc-900/50 border-white/10 text-xs"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="growth">Sort: Growth %</SelectItem>
                                                <SelectItem value="volume">Sort: Volume</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <div className="flex gap-1 bg-zinc-900/50 p-1 rounded-lg border border-white/10">
                                            {[['cards', ViewGridIcon, 'Cards'], ['table', TableIcon, 'Table']].map(([v, Icon, label]) => (
                                                <button key={v} onClick={() => setViewMode(v)} className={`px-3 py-1 rounded text-xs font-medium transition-all flex items-center gap-1.5 ${viewMode === v ? 'bg-white text-zinc-950' : 'text-zinc-400 hover:text-zinc-200'}`}>
                                                    <Icon className="w-3.5 h-3.5" /> {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Category mode: New Alerts as featured cards */}
                                {mode === MODE_CATEGORY && newAlerts.length > 0 && (
                                    <div className="space-y-4">
                                        <h3 className="text-sm font-semibold text-orange-400 flex items-center gap-2">
                                            <BellIcon className="w-4 h-4" /> New Alerts ({newAlerts.length})
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {newAlerts.map(t => <NewAlertCard key={t.id} topic={t} />)}
                                        </div>
                                    </div>
                                )}

                                {/* Category mode: Active alerts as compact list */}
                                {mode === MODE_CATEGORY && activeAlerts.length > 0 && (
                                    <Card className="bg-zinc-900/40 backdrop-blur-xl border-white/10">
                                        <CardHeader>
                                            <CardTitle className="text-lg text-white flex items-center gap-2">⚡ Active Alerts ({activeAlerts.length})</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2">
                                                {activeAlerts.map(t => <ActiveAlertItem key={t.id} topic={t} />)}
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Keyword mode: Cards view */}
                                {mode === MODE_KEYWORD && viewMode === 'cards' && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {filtered.map((trend) => {
                                            const g = getGrowthLevel(trend.growth);
                                            const GIcon = g.icon;
                                            const d = getDifficultyLevel(trend.difficulty);
                                            return (
                                                <Card key={trend.id} className="hover:border-violet-500/50 transition-all duration-300">
                                                    <CardHeader className="pb-3">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className={`text-lg font-bold ${g.class}`}>#{trend.id}</span>
                                                            <Badge variant="outline" className={`gap-1 ${g.class} border ${g.border} ${g.bg}`}>
                                                                <GIcon className="w-3 h-3" /> {g.label}
                                                            </Badge>
                                                        </div>
                                                        <h4 className="font-semibold text-base text-white">{trend.keyword}</h4>
                                                    </CardHeader>
                                                    <CardContent className="space-y-3">
                                                        <div className={`text-4xl font-bold ${g.class}`}>{trend.growthLabel}</div>
                                                        <div className="grid grid-cols-3 gap-2">
                                                            <div className="bg-muted/50 rounded-lg p-3 text-center">
                                                                <div className="text-xs text-muted-foreground mb-1">Interest</div>
                                                                <div className="text-lg font-bold text-white">{trend.interest}</div>
                                                                <div className="text-[10px] text-muted-foreground">/100</div>
                                                            </div>
                                                            <div className="bg-muted/50 rounded-lg p-3 text-center">
                                                                <div className="text-xs text-muted-foreground mb-1">Volume</div>
                                                                <div className="text-sm font-bold text-white truncate">{trend.volume}</div>
                                                            </div>
                                                            <div className="bg-muted/50 rounded-lg p-3 text-center">
                                                                <div className="text-xs text-muted-foreground mb-1">Difficulty</div>
                                                                <div className="text-sm font-bold text-white">{trend.difficulty ?? 'N/A'}</div>
                                                                <div className={`mt-1 inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium ${d.bg} ${d.class}`}>{d.label}</div>
                                                            </div>
                                                        </div>
                                                        <Button onClick={() => handleAddToQueue(trend.keyword)} className="w-full" size="sm">
                                                            <PlusIcon className="w-4 h-4 mr-2" /> Add to Queue
                                                        </Button>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Keyword mode: Table view */}
                                {mode === MODE_KEYWORD && viewMode === 'table' && (
                                    <div className="rounded-lg border border-white/10 overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead className="bg-zinc-900/50 border-b border-white/10">
                                                    <tr>
                                                        {['Rank', 'Keyword', 'Growth', 'Interest', 'Volume', 'Difficulty', 'Actions'].map(h => (
                                                            <th key={h} className={`py-3 px-4 text-xs font-medium text-zinc-400 ${h === 'Actions' ? 'text-right' : h === 'Rank' || h === 'Keyword' ? 'text-left' : 'text-center'}`}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-white/5">
                                                    {filtered.map((trend) => {
                                                        const g = getGrowthLevel(trend.growth);
                                                        const GIcon = g.icon;
                                                        const d = getDifficultyLevel(trend.difficulty);
                                                        return (
                                                            <tr key={trend.id} className="hover:bg-white/5 transition-colors">
                                                                <td className="py-3 px-4"><span className={`text-sm font-bold ${g.class}`}>#{trend.id}</span></td>
                                                                <td className="py-3 px-4 font-medium text-sm text-white">{trend.keyword}</td>
                                                                <td className="py-3 px-4">
                                                                    <Badge variant="outline" className={`gap-1 ${g.class} border ${g.border} ${g.bg} text-xs`}>
                                                                        <GIcon className={`w-3 h-3 ${g.class}`} /> {trend.growthLabel}
                                                                    </Badge>
                                                                </td>
                                                                <td className="py-3 px-4 text-center text-sm text-white">{trend.interest}<span className="text-xs text-zinc-500">/100</span></td>
                                                                <td className="py-3 px-4 text-center text-sm text-white">{trend.volume}</td>
                                                                <td className="py-3 px-4 text-center">
                                                                    <div className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${d.bg} ${d.class}`}>{trend.difficulty ?? 'N/A'} {d.label}</div>
                                                                </td>
                                                                <td className="py-3 px-4 text-right">
                                                                    <Button onClick={() => handleAddToQueue(trend.keyword)} size="sm" variant="outline">
                                                                        <PlusIcon className="w-3 h-3 mr-1" /> Add
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {filtered.length === 0 && (
                                    <div className="text-center py-12">
                                        <p className="text-zinc-400">No trending topics match your filters.</p>
                                    </div>
                                )}

                                {filtered.length > 0 && (
                                    <div className="flex items-center justify-between text-sm text-zinc-500 pt-2">
                                        <span>{filtered.length} results</span>
                                        <span>Sorted by {sortBy === 'growth' ? 'Growth %' : 'Volume'}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </main>

                {showQuickPost && (
                    <QuickPost
                        user={user}
                        onClose={(dest) => { setShowQuickPost(false); setPrefilledTopic(''); setPrefilledLocation(''); if (dest === 'topics') navigate('/topic-queue'); }}
                        prefilledTopic={prefilledTopic}
                        prefilledLocation={prefilledLocation}
                        hideKeywordField={true}
                    />
                )}
        </div>
    );
}

export default TrendDiscovery;
